import { useMemo } from 'react';
import { Button, Drawer, Empty, List, Space, Tag, Typography } from 'antd';
import { ClockCircleOutlined, DeleteOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { semantic } from '@/theme/tokens';
import { clearRouteRecents, type RouteRecent } from './recents';
import { resolveRouteLabelKey } from './routeLabels';

dayjs.extend(relativeTime);

const { Text } = Typography;

interface RecentsDrawerProps {
  open: boolean;
  onClose: () => void;
  recents: RouteRecent[];
  /** Notifies parent to refresh its in-memory list after clear. */
  onCleared: () => void;
}

/**
 * S4-T06 — Recents drawer.
 *
 * Right-side (or left in RTL) drawer showing chronological route history
 * captured by useTrackRecents in AppShell. Pairs with ⌘K (T01) and saved
 * views (T04) to complete the "I know where I was" trio.
 *
 * Source of truth: src/shell/recents.ts (localStorage 'ga-erp-route-recents').
 */
export default function RecentsDrawer({ open, onClose, recents, onCleared }: RecentsDrawerProps) {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const isRtl = i18n.language?.startsWith('ar');

  // Use AR-aware dayjs locale for relative timestamps. Falls back to EN
  // formatting if dayjs/ar isn't loaded — App.tsx already imports both.
  const ChevronIcon = isRtl ? LeftOutlined : RightOutlined;

  const items = useMemo(
    () =>
      recents.map((r) => {
        const labelKey = resolveRouteLabelKey(r.path);
        const label = labelKey
          ? t(labelKey, { defaultValue: r.path })
          : r.path;
        return { ...r, label };
      }),
    [recents, t],
  );

  const handleClick = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleClear = () => {
    clearRouteRecents();
    onCleared();
  };

  return (
    <Drawer
      title={
        <Space>
          <ClockCircleOutlined style={{ color: semantic.brand.accent }} />
          <span>{t('recents.title')}</span>
          <Tag
            style={{
              marginInlineStart: 4,
              background: 'rgba(184, 137, 62, 0.12)',
              color: semantic.brand.primary,
              border: 'none',
            }}
          >
            {recents.length}
          </Tag>
        </Space>
      }
      placement={isRtl ? 'left' : 'right'}
      open={open}
      onClose={onClose}
      width={360}
      destroyOnHidden
      footer={
        items.length > 0 ? (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('recents.footer')}
            </Text>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={handleClear}>
              {t('recents.clear')}
            </Button>
          </Space>
        ) : null
      }
    >
      {items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size={4} align="center">
              <Text>{t('recents.emptyTitle')}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('recents.emptyHint')}
              </Text>
            </Space>
          }
        />
      ) : (
        <List
          dataSource={items}
          renderItem={(item) => (
            <List.Item
              key={item.path}
              onClick={() => handleClick(item.path)}
              style={{ cursor: 'pointer', padding: '10px 8px' }}
              actions={[<ChevronIcon key="go" style={{ color: 'rgba(0,0,0,0.35)' }} />]}
            >
              <List.Item.Meta
                title={<span style={{ fontWeight: 500 }}>{item.label}</span>}
                description={
                  <Space size={6}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(item.visitedAt).fromNow()}
                    </Text>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        fontFamily: 'monospace',
                        opacity: 0.6,
                      }}
                    >
                      {item.path}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
}
